/**
 * Reading a push message, free of the service worker so it can be tested.
 */

export interface NotificationContent {
  readonly title: string;
  readonly body: string;
  readonly tag: string | undefined;
  /** Always on this app's origin. */
  readonly url: string;
}

const FALLBACK_TITLE = 'Glasfaser Vertrieb';

/**
 * Turns whatever arrived into something safe to show.
 *
 * The payload comes from our server, but the worker should not trust a
 * value it is about to open in a window: a URL on another origin would
 * turn a tap on a reminder into a trip to somewhere else. Anything that
 * is not a same-origin path falls back to the app's start page.
 */
export function readPushPayload(raw: unknown, origin: string): NotificationContent {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

  const title = typeof data.title === 'string' && data.title.trim() ? data.title : FALLBACK_TITLE;
  const body = typeof data.body === 'string' ? data.body : '';
  const tag = typeof data.tag === 'string' && data.tag ? data.tag : undefined;

  return { title, body, tag, url: sameOriginUrl(data.url, origin) };
}

export function sameOriginUrl(value: unknown, origin: string): string {
  if (typeof value !== 'string') return `${origin}/`;
  try {
    const url = new URL(value, origin);
    return url.origin === origin ? url.href : `${origin}/`;
  } catch {
    return `${origin}/`;
  }
}

/**
 * The VAPID public key, from the base64url text the server sends into the
 * byte array PushManager.subscribe expects.
 */
export function urlBase64ToBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
